import React, { useState } from 'react';
import { NavLink } from 'react-router-dom';
import { Box, Stack, Typography } from '@mui/material';
import { styled } from '@mui/material/styles';
import OverviewIcon from '../Dashboard/icons/OverviewIcon';
import PerformanceIcon from '../Dashboard/icons/PerformanceIcon';
import ReputationIcon from '../Dashboard/icons/ReputationIcon';
import EDMIcon from '../Dashboard/icons/EDMIcon';
import WebsiteIcon from '../Dashboard/icons/WebsiteIcon';
import WhatsAppIcon from '../Dashboard/icons/WhatsAppIcon';
import BlogIcon from '../Dashboard/icons/BlogIcon';
import SocialMediaIcon from '../Dashboard/icons/SocialMediaIcon';
import ReferEarnIcon from '../Dashboard/icons/ReferEarnIcon';
import MyAccountIcon from '../Dashboard/icons/MyAccountIcon';
import TaskManagementIcon from '../Dashboard/icons/TaskManagementIcon';
import { usePermissions } from '../../contexts/PermissionContext';
import { useProfilePlan } from '../../contexts/ProfilePlanContext';
import { useAccessGate } from '../../contexts/AccessGateContext';
import WhitelistUpgradeModal from '../shared/WhitelistUpgradeModal';
import ProfileSelectorModal from '../shared/ProfileSelectorModal';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import AddTaskIcon from '@mui/icons-material/AddTask';

// Use AddTaskIcon as RequestsIcon
const RequestsIcon = AddTaskIcon;

const SidebarContainer = styled(Box)(({ theme }) => ({
  width: '325px',
  height: '100%',
  backgroundColor: '#ffffff',
  display: 'flex',
  flexDirection: 'column',
  padding: '16px 0',
  borderRight: '1px solid #f0f0f0',
  overflowY: 'auto',
  overflowX: 'hidden',
  WebkitOverflowScrolling: 'touch',
  '&::-webkit-scrollbar': {
    width: '6px'
  },
  '&::-webkit-scrollbar-track': {
    background: 'transparent'
  },
  '&::-webkit-scrollbar-thumb': {
    background: '#d1d5db',
    borderRadius: '3px'
  },
  '&::-webkit-scrollbar-thumb:hover': {
    background: '#9ca3af'
  },
  [theme.breakpoints.down('md')]: {
    width: '100%',
    maxWidth: '280px',
    borderRight: 'none',
    padding: '12px 0'
  }
}));

const NavSection = styled(Stack)(({ theme }) => ({
  gap: '8px',
  padding: '0 16px',
  [theme.breakpoints.up('md')]: {
    gap: '12px',
    padding: '0 20px'
  }
}));

const NavItem = styled(NavLink)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: '12px',
  padding: '10px 12px',
  borderRadius: '6px',
  textDecoration: 'none',
  color: '#121927',
  transition: 'all 0.2s ease',
  minHeight: '44px',
  WebkitTapHighlightColor: 'transparent',
  '&:active': {
    backgroundColor: '#e5e7eb',
    transform: 'scale(0.98)'
  },
  '&.active': {
    backgroundColor: '#EFF6FF',
    '& .nav-icon': {
      color: '#0B91D6'
    },
    '& .nav-text': {
      color: '#0B91D6'
    }
  },
  [theme.breakpoints.up('md')]: {
    gap: '16px',
    padding: '12px 16px',
    borderRadius: '4px',
    '&:hover': {
      backgroundColor: '#f5f5f5'
    },
    '&:active': {
      transform: 'none'
    }
  }
}));

const NavText = styled(Typography)(({ theme }) => ({
  fontFamily: 'Inter, sans-serif',
  fontSize: '14px',
  fontWeight: 500,
  color: 'inherit',
  [theme.breakpoints.up('md')]: {
    fontSize: '16px'
  }
}));

const ComingSoonBadge = styled(Typography)(({ theme }) => ({
  fontFamily: 'Inter, sans-serif',
  fontSize: '10px',
  fontWeight: 400,
  color: '#EF232A',
  marginLeft: 'auto',
  whiteSpace: 'nowrap',
  [theme.breakpoints.up('md')]: {
    fontSize: '12px'
  }
}));

const SectionDivider = styled(Box)(({ theme }) => ({
  height: '1px',
  backgroundColor: '#f0f0f0',
  margin: '12px 0',
  [theme.breakpoints.up('md')]: {
    margin: '20px 0'
  }
}));

const DashboardSidebar = ({ onNavigate }) => {
  // eslint-disable-next-line no-unused-vars
  const [edmsOpen, setEdmsOpen] = useState(false);
  const [accountOpen, setAccountOpen] = useState(false);
  // eslint-disable-next-line no-unused-vars
  const [selectedFeature, setSelectedFeature] = useState('');
  const [upgradeModalOpen, setUpgradeModalOpen] = useState(false);
  const [upgradeFeatureName, setUpgradeFeatureName] = useState('');
  const [upgradeModalType, setUpgradeModalType] = useState('whitelist'); // 'whitelist' or 'subscription'
  const [profileSelectorOpen, setProfileSelectorOpen] = useState(false);
  const [targetRoute, setTargetRoute] = useState('');
  // eslint-disable-next-line no-unused-vars
  const { isAdmin, loading: permissionsLoading, triggerUpgradeNotice } = usePermissions();
  // eslint-disable-next-line no-unused-vars
  const { hasFeature, planCode, hasSubscription, isLoading: planLoading } = useProfilePlan();
  const { isWhitelisted, isChecking: accessChecking } = useAccessGate();

  // Two-layer access control:
  // 1. Platform Access: isWhitelisted (from allowed_emails table) OR hasPlan (from GMB profile)
  // 2. Feature Access: hasFeature (from GMB profile's plan)

  // Users can access features if:
  // - They are admin, OR
  // - They are in allowed_emails (whitelisted), OR
  // - They have a plan assigned to their GMB profile
  const hasPlanAssigned = hasSubscription && planCode;
  const hasPlatformAccess = isAdmin || isWhitelisted || hasPlanAssigned;

  // eslint-disable-next-line no-unused-vars
  const toggleEdms = (e) => {
    e.preventDefault();
    setEdmsOpen(!edmsOpen);
  };
  const toggleAccount = (e) => {
    e.preventDefault();
    setAccountOpen(!accountOpen);
  };

  const handleNavClick = () => {
    if (onNavigate) {
      onNavigate();
    }
  };

  const handleRestrictedFeatureClick = (e, featureName, featureLabel, route) => {
    console.log('🔒 [Sidebar] Feature click:', {
      featureName,
      featureLabel,
      route,
      isAdmin,
      accessChecking,
      planLoading,
      hasPlatformAccess,
      isWhitelisted,
      hasPlanAssigned,
      planCode
    });

    // Admins bypass profile selection
    if (isAdmin) {
      console.log('🔓 [Sidebar] Admin access granted');
      handleNavClick();
      return true;
    }

    // Block navigation while access check is still loading
    if (accessChecking || planLoading) {
      console.log('⏳ [Sidebar] Still checking access/plan, blocking...');
      e.preventDefault();
      return false;
    }

    // Check whitelist OR plan - user must have either one
    if (!hasPlatformAccess) {
      console.log('❌ [Sidebar] BLOCKED - No whitelist access AND no plan assigned');
      e.preventDefault();
      setUpgradeFeatureName(featureLabel);
      setUpgradeModalType('subscription'); // Show subscription modal for users without plan
      setUpgradeModalOpen(true);
      return false;
    }

    // For Monthly Plan, check subscription
    if (featureName === 'monthly-plan') {
      console.log('🔍 [Sidebar] Checking subscription for Monthly Plan:', {
        hasSubscription,
        planCode,
        planLoading
      });

      if (planLoading) {
        console.log('⏳ [Sidebar] Plan still loading, blocking...');
        e.preventDefault();
        return false;
      }

      if (!hasSubscription || !planCode) {
        console.log('❌ [Sidebar] BLOCKED - No active subscription for Monthly Plan');
        e.preventDefault();
        setUpgradeFeatureName(featureLabel);
        setUpgradeModalType('subscription');
        setUpgradeModalOpen(true);
        return false;
      }

      console.log('✅ [Sidebar] Has subscription - Access granted to Monthly Plan');
      handleNavClick();
      return true;
    }

    // For profile-based features, show profile selector
    const profileBasedFeatures = ['performance', 'reputation', 'task-management', 'free-website'];
    if (profileBasedFeatures.includes(featureName)) {
      console.log('🔍 [Sidebar] Profile-based feature - showing profile selector');
      e.preventDefault();
      setTargetRoute(route);
      setProfileSelectorOpen(true);
      return false;
    }

    // Default: allow access
    console.log('✅ [Sidebar] Access granted for:', featureName);
    handleNavClick();
    return true;
  };

  return (
    <>
      <WhitelistUpgradeModal
        open={upgradeModalOpen}
        onClose={() => setUpgradeModalOpen(false)}
        featureName={upgradeFeatureName}
        modalType={upgradeModalType}
      />
      <ProfileSelectorModal
        open={profileSelectorOpen}
        onClose={() => setProfileSelectorOpen(false)}
        targetRoute={targetRoute}
      />
      <SidebarContainer>
        <NavSection>
          <NavItem to="/dashboard" className={({ isActive }) => (isActive ? 'active' : '')} onClick={handleNavClick}>
            <OverviewIcon className="nav-icon" width={16} height={16} color="currentColor" />
            <NavText className="nav-text">Overview</NavText>
          </NavItem>

          <NavItem
            to="/performance"
            className={({ isActive }) => (isActive ? 'active' : '')}
            onClick={(e) => handleRestrictedFeatureClick(e, 'performance', 'Performance', '/performance')}
            data-tutorial="performance-nav"
          >
            <PerformanceIcon className="nav-icon" width={16} height={16} color="currentColor" />
            <NavText className="nav-text">Performance</NavText>
          </NavItem>

          <NavItem
            to="/monthly-plan"
            className={({ isActive }) => (isActive ? 'active' : '')}
            onClick={(e) => handleRestrictedFeatureClick(e, 'monthly-plan', 'Monthly Plan')}
            data-tutorial="monthly-plan-nav"
          >
            <CalendarMonthIcon className="nav-icon" sx={{ fontSize: 20, color: 'currentColor' }} />
            <NavText className="nav-text">Monthly Plan</NavText>
          </NavItem>

          <NavItem
            to="/reputation"
            className={({ isActive }) => (isActive ? 'active' : '')}
            onClick={(e) => handleRestrictedFeatureClick(e, 'reputation', 'Reputation Management', '/reputation')}
            data-tutorial="reputation-nav"
          >
            <ReputationIcon className="nav-icon" width={16} height={16} color="currentColor" />
            <NavText className="nav-text">Reputation Management</NavText>
          </NavItem>

          <NavItem
            to="/task-management"
            className={({ isActive }) => (isActive ? 'active' : '')}
            onClick={(e) => handleRestrictedFeatureClick(e, 'task-management', 'Task Management', '/task-management')}
          >
            <TaskManagementIcon className="nav-icon" width={16} height={16} color="currentColor" />
            <NavText className="nav-text">Task Management</NavText>
          </NavItem>

          <NavItem
            to="/get-edms"
            className={({ isActive }) => (isActive ? 'active' : '')}
            onClick={handleNavClick}
          >
            <EDMIcon className="nav-icon" width={16} height={16} color="currentColor" />
            <NavText className="nav-text">Get EDMs</NavText>
          </NavItem>

          <NavItem
            to="/free-website"
            className={({ isActive }) => (isActive ? 'active' : '')}
            onClick={(e) => handleRestrictedFeatureClick(e, 'free-website', 'Free Website', '/free-website')}
            data-tutorial="free-website-nav"
          >
            <WebsiteIcon className="nav-icon" width={16} height={16} color="currentColor" />
            <NavText className="nav-text">Free Website</NavText>
          </NavItem>

          {/* Removed 'Your Data' from sidebar as requested */}
        </NavSection>
        <SectionDivider />
        <NavSection>
          <Box sx={{
            display: 'flex',
            alignItems: 'center',
            gap: { xs: '12px', md: '16px' },
            padding: { xs: '10px 12px', md: '12px 16px' },
            minHeight: '44px'
          }}>
            <WhatsAppIcon className="nav-icon" width={16} height={16} color="#000000" />
            <NavText className="nav-text">Whats app</NavText>
            <ComingSoonBadge>(Coming soon)</ComingSoonBadge>
          </Box>

          <Box sx={{
            display: 'flex',
            alignItems: 'center',
            gap: { xs: '12px', md: '16px' },
            padding: { xs: '10px 12px', md: '12px 16px' },
            minHeight: '44px'
          }}>
            <BlogIcon className="nav-icon" width={16} height={16} color="#121927" />
            <NavText className="nav-text">Blogs</NavText>
            <ComingSoonBadge>(Coming soon)</ComingSoonBadge>
          </Box>

          <Box sx={{
            display: 'flex',
            alignItems: 'center',
            gap: { xs: '12px', md: '16px' },
            padding: { xs: '10px 12px', md: '12px 16px' },
            minHeight: '44px'
          }}>
            <SocialMediaIcon className="nav-icon" width={16} height={16} color="#121927" />
            <NavText className="nav-text">Social Media</NavText>
            <ComingSoonBadge>(Coming soon)</ComingSoonBadge>
          </Box>

          <NavItem
            to="/refer-earn"
            className={({ isActive }) => (isActive ? 'active' : '')}
            onClick={(e) => handleRestrictedFeatureClick(e, 'refer-earn', 'Refer & Earn')}
          >
            <ReferEarnIcon className="nav-icon" width={16} height={16} color="currentColor" />
            <NavText className="nav-text">Refer & Earn</NavText>
          </NavItem>

          {/* My Account Dropdown */}
          <div onClick={toggleAccount} style={{ cursor: 'pointer' }}>
            <NavItem as="div" to="/my-account">
              <MyAccountIcon className="nav-icon" width={16} height={16} color="currentColor" />
              <NavText className="nav-text">My Account</NavText>
              <Box sx={{ marginLeft: 'auto' }}>{accountOpen ? '\u25B2' : '\u25BC'}</Box>
            </NavItem>
          </div>
          {accountOpen && (
            <Stack sx={{ pl: { xs: 3, md: 4 }, gap: { xs: '4px', md: '8px' } }}>
              <NavItem to="/my-account/account-information" className={({ isActive }) => (isActive ? 'active' : '')} onClick={handleNavClick}>
                <NavText className="nav-text">Account Information</NavText>
              </NavItem>
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: { xs: '12px', md: '16px' },
                  padding: { xs: '10px 12px', md: '12px 16px' },
                  borderRadius: { xs: '6px', md: '4px' },
                  color: '#999',
                  cursor: 'not-allowed',
                  opacity: 0.7,
                  minHeight: '44px'
                }}
              >
                <NavText className="nav-text" sx={{ color: '#999' }}>GST Information</NavText>
                <Typography
                  sx={{
                    fontSize: { xs: '9px', md: '10px' },
                    backgroundColor: '#f0f0f0',
                    color: '#666',
                    padding: '2px 6px',
                    borderRadius: '4px',
                    marginLeft: 'auto',
                    whiteSpace: 'nowrap'
                  }}
                >
                  Coming Soon
                </Typography>
              </Box>
              <NavItem to="/contact-us" className={({ isActive }) => (isActive ? 'active' : '')} onClick={handleNavClick}>
                <NavText className="nav-text">Contact Us</NavText>
              </NavItem>
            </Stack>
          )}
        </NavSection>

        {/* upgrade notice handled by PermissionProvider (Snackbar) */}
      </SidebarContainer>
    </>
  );
};

export default DashboardSidebar;
